import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
/*
 * The media the currently selected variant should be showing.
 *
 * Held at module scope on purpose: selecting a variant triggers an async section fetch,
 * and `#handleProductSelect` then swaps this element out via `replaceWith`, so anything
 * stored on the instance is gone by the time the new gallery mounts. The new instance
 * re-applies this on connect, which is what makes the gallery land on the variant's
 * image rather than snapping back to the first slide.
 */
let pendingVariantMediaId = null;

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener('variant:url-sync', this.#handleVariantUrlSync, { signal });

    /* Deferred: on a fresh mount the slideshow has not wired up its slides yet. */
    if (pendingVariantMediaId) {
      requestAnimationFrame(() => this.#showVariantMedia());
    }
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /**
   * Points the gallery at the media belonging to the newly selected variant.
   *
   * `variant-picker.js` emits `variant:url-sync`; two ingredient blocks already
   * listened for it, but nothing dispatched it after the upgrade and this gallery had
   * lost its handler, so choosing a variant left the gallery where it was.
   *
   * @param {Event} event - The variant:url-sync event.
   */
  #handleVariantUrlSync = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const mediaId = event.detail?.mediaId;
    if (!mediaId) return;

    pendingVariantMediaId = String(mediaId);
    this.#showVariantMedia();
  };

  /**
   * Points the slideshow at `pendingVariantMediaId`, if a slide carries that id.
   *
   * Selecting by id is a no-op unless a slide has the matching `slide-id`, which
   * `product-media-gallery-content.liquid` supplies from `media.id`.
   */
  #showVariantMedia() {
    if (!pendingVariantMediaId) return;

    this.slideshow?.select({ id: pendingVariantMediaId }, undefined, { animate: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
